import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Scale, FileText, UserCheck, Lock } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { COMPLIANCE_INFO } from '@/data/index';
import { ComplianceCard } from '@/components/Cards';
import { IMAGES } from '@/assets/images';

/**
 * Compliance Page
 * 
 * This page highlights the company's commitment to Japanese corporate laws,
 * tax management, labor standards, and data privacy. It serves as a critical
 * reference for banking institutions and business partners during credit reviews.
 */
const Compliance: React.FC = () => {
  const { isEn } = useLanguage();

  // Mapping data keys (ja -> jp) for consistency with the useLanguage hook expectation if needed,
  // but here we access the ContentPair directly using the isEn flag.
  const getLangText = (pair: { ja: string; en: string }) => (isEn ? pair.en : pair.ja);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[40vh] min-h-[300px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.OFFICE_BUILDING_6}
            alt="Compliance"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background" />
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 text-primary">
              {isEn ? 'Compliance' : 'コンプライアンス'}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              {isEn 
                ? 'Upholding the highest standards of integrity and legal adherence in Japan.' 
                : '日本国内の法令を遵守し、誠実かつ透明性の高い事業運営を徹底します。'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-16 md:py-24 space-y-24">
        
        {/* Core Framework Section */}
        <section className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <ShieldCheck className="w-4 h-4" />
                <span>{isEn ? 'Governance' : 'ガバナンス体制'}</span>
              </div>
              <h2 className="text-3xl font-bold mb-6">
                {getLangText(COMPLIANCE_INFO.title)}
              </h2>
            </div>
            <div className="md:col-span-7">
              <p className="text-lg leading-relaxed text-muted-foreground mb-8">
                {getLangText(COMPLIANCE_INFO.statement)}
              </p>
              <div className="grid gap-4">
                {COMPLIANCE_INFO.points.map((point, index) => (
                  <ComplianceCard 
                    key={index} 
                    info={{ 
                      title: { jp: point.ja, en: point.en }, 
                      content: { jp: '', en: '' } 
                    }} 
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Detailed Pillars Section */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <motion.div 
            whileHover={{ y: -5 }}
            className="p-8 rounded-lg bg-card border border-border shadow-sm"
          >
            <Scale className="w-10 h-10 text-primary mb-6" />
            <h3 className="text-xl font-bold mb-4">
              {isEn ? 'Tax & Accounting' : '適切な税務・会計'}
            </h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {isEn 
                ? 'We ensure rigorous tax reporting and fulfillment of all fiscal obligations in accordance with Japanese tax law, maintaining high financial transparency.'
                : '日本国内の税法に基づき、適切な税務申告と納税義務の履行を徹底しています。透明性の高い会計処理により、健全な財務体質を維持します。'}
            </p>
          </motion.div>

          <motion.div 
            whileHover={{ y: -5 }}
            className="p-8 rounded-lg bg-card border border-border shadow-sm"
          >
            <UserCheck className="w-10 h-10 text-primary mb-6" />
            <h3 className="text-xl font-bold mb-4">
              {isEn ? 'Labor Standards' : '適正な労務管理'}
            </h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {isEn
                ? 'Adhering strictly to the Labor Standards Act, we prioritize a healthy work environment and fair treatment for all employees and contractors.'
                : '労働基準法を厳守し、従業員の福祉と適切な労働条件の確保に努めています。法令に基づいた雇用契約と就業環境の整備を継続的に実施します。'}
            </p>
          </motion.div>

          <motion.div 
            whileHover={{ y: -5 }}
            className="p-8 rounded-lg bg-card border border-border shadow-sm"
          >
            <Lock className="w-10 h-10 text-primary mb-6" />
            <h3 className="text-xl font-bold mb-4">
              {isEn ? 'Privacy & Data' : '個人情報保護'}
            </h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {isEn
                ? 'In compliance with the Act on the Protection of Personal Information (APPI), we implement strict protocols for data handling and security.'
                : '個人情報保護法に基づき、お客様および取引先様の情報を厳重に管理しています。情報セキュリティ対策を講じ、データの適正な取り扱いを保証します。'}
            </p>
          </motion.div>
        </section>

        {/* Corporate Ethics Footer Section */}
        <section className="bg-muted/30 rounded-2xl p-8 md:p-12 text-center max-w-4xl mx-auto border border-border/50">
          <FileText className="w-12 h-12 text-primary/40 mx-auto mb-6" />
          <h3 className="text-2xl font-bold mb-4">
            {isEn ? 'Commitment to Integrity' : '誠実な経営への誓い'}
          </h3>
          <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {isEn 
              ? 'As a Japanese corporation, M.M.K Co., Ltd. is committed to sustainable growth through ethical practices, contributing to social stability and building long-term trust with our global partners.'
              : '株式会社M.M.Kは、日本法人として、倫理的な慣行を通じて持続可能な成長を目指します。社会の安定に寄与し、グローバルパートナーとの長期的な信頼関係を築くことをお約束します。'}
          </p>
        </section>
      </main>
    </div>
  );
};

export default Compliance;